import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from './UniqueUsersChart.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export default function UniqueUsersChart({ data }) {
    const deviceData = data?.deviceData || { mobile: 0, desktop: 0, tablet: 0, other: 0 };
    const mobileBreakdown = data?.mobileBreakdown || { ios: 0, android: 0, other: 0 };
    const phoneModels = data?.phoneModels || {};
    const totalScans = Object.values(deviceData).reduce((sum, count) => sum + count, 0);
    
    // Calculate iOS vs Android percentages based on just iOS + Android (not including "other mobile")
    const iosAndroidTotal = mobileBreakdown.ios + mobileBreakdown.android;
    const iosPercentage = iosAndroidTotal > 0 ? Math.round((mobileBreakdown.ios / iosAndroidTotal) * 100) : 0;
    const androidPercentage = iosAndroidTotal > 0 ? Math.round((mobileBreakdown.android / iosAndroidTotal) * 100) : 0;
    
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 0,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#161d2d',
                titleColor: '#f6f6f6',
                bodyColor: '#f6f6f6',
                borderColor: '#161d2d',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    label: function(context) {
                        const percentage = totalScans > 0 ? Math.round((context.parsed.y / totalScans) * 100) : 0;
                        return `${context.label}: ${context.parsed.y} scans (${percentage}%)`;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f3f4f6',
                    borderDash: [2, 2],
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 10,
                    },
                    maxTicksLimit: 4,
                },
            },
        },
    };

    // Show iOS vs Android breakdown for mobile-focused QR analytics
    const chartData = {
        labels: ['iOS', 'Android', 'Other Mobile', 'Desktop+'],
        datasets: [
            {
                label: 'Device Breakdown',
                data: [
                    mobileBreakdown.ios, 
                    mobileBreakdown.android, 
                    mobileBreakdown.other,
                    deviceData.desktop + deviceData.tablet + deviceData.other
                ],
                backgroundColor: [
                    '#007AFF', // iOS - Apple Blue
                    '#34A853', // Android - Google Green
                    '#9ca3af', // Other Mobile - Gray
                    '#6b7280'  // Desktop+ - Dark Gray
                ],
                borderRadius: 4,
                borderSkipped: false,
            },
        ],
    };

    const currentUsers = data?.current || 0;
    
    // Get top phone models
    const topPhoneModels = Object.entries(phoneModels)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>User Insights</h3>
                <div className={styles.value}>{currentUsers}</div>
            </div>
            
            <div className={styles.deviceSummary}>
                <div className={styles.deviceStat}>
                    <i className="fa-brands fa-apple"></i>
                    <span>{iosPercentage}% iOS</span>
                </div>
                <div className={styles.deviceStat}>
                    <i className="fa-brands fa-android"></i>
                    <span>{androidPercentage}% Android</span>
                </div>
            </div>
            
            <div className={styles.chartContainer}>
                <Bar 
                    options={options} 
                    data={chartData}
                />
            </div>
            
            <div className={styles.insights}>
                <div className={styles.insight}>
                    <div className={styles.insightIcon}>
                        <i className="fa-solid fa-users"></i>
                    </div>
                    <div className={styles.insightText}>
                        <div className={styles.insightValue}>{currentUsers}</div>
                        <div className={styles.insightLabel}>Unique IPs</div>
                    </div>
                </div>
                
                <div className={styles.insight}>
                    <div className={styles.insightIcon}>
                        <i className="fa-solid fa-mobile-screen-button"></i>
                    </div>
                    <div className={styles.insightText}>
                        <div className={styles.insightValue}>
                            {topPhoneModels.length > 0 ? topPhoneModels[0][1] : 0}
                        </div>
                        <div className={styles.insightLabel}>
                            {topPhoneModels.length > 0 ? 'Top Device: ' + topPhoneModels[0][0] : 'No Data'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
} 